import { DeviceDocumentType } from "@/__generated__/graphql";
import { type Device } from "@features/createDeviceConfiguration/graphql/querys/getUpdateHardwareConfigurationData";
import { setSaveHardwareConfigurationModal } from "@/slices/modals";
import { FormInputDate, FormInputText } from "@components/form";
import ImagesNews from "@components/upload/ImagesNews";
import { FormValues } from "@features/createDeviceConfiguration/type/FormValues";
import GET_DEVICE_VPN_CERTIFICATE_URL from "@features/createDeviceConfiguration/graphql/querys/getDeviceVpnCertificateUrl";
import GET_DEVICE_CERTIFICATE_EXISTS from "@features/createDeviceConfiguration/graphql/querys/getDeviceCertificateExists";
import CREATE_DEVICE_VPN_CERTIFICATE from "@features/createDeviceConfiguration/graphql/mutations/createDeviceVpnCertificate";
import { Link } from "@mui/icons-material";
import { Alert, Box, Button, Chip, Grid, Tooltip, Typography, Link as LinkMUI } from "@mui/material";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { Control, Controller, UseFormSetValue, useWatch } from "react-hook-form";
import { useState } from "react";
import dayjs from "dayjs";
import { useDispatch } from "react-redux";
import { PDFUploadField } from "../../common/PDFUploadField";
import { useLingui } from "@lingui/react";
import { Trans } from "@lingui/macro";

interface GeneralTabContentProps {
  device: Device;
  control: Control<FormValues>;
  isChildDevice: boolean;
  setValue: UseFormSetValue<FormValues>;
  readOnly?: boolean;
}

const GeneralTabContent = ({ device, control, isChildDevice, setValue, readOnly = false }: GeneralTabContentProps) => {
  const disabled = isChildDevice || readOnly;
  const dispatch = useDispatch();
  const { _ } = useLingui();
  const [certificateRequested, setCertificateRequested] = useState(false);
  const { data: certificateExistsData, loading: certificateExistsLoading } = useQuery(GET_DEVICE_CERTIFICATE_EXISTS, {
    variables: { deviceId: device.id },
    fetchPolicy: 'network-only',
  });
  const certificateExists = certificateExistsData?.deviceCertificateExists ?? false;

  const [fetchCertificateUrl, { loading: certificateLoading }] = useLazyQuery(
    GET_DEVICE_VPN_CERTIFICATE_URL,
  );

  const [createVpnCertificate, { loading: createCertificateLoading }] = useMutation(
    CREATE_DEVICE_VPN_CERTIFICATE,
    {
      onCompleted() {
        setCertificateRequested(true);
      },
    },
  );

  const handleDownloadCertificate = async () => {
    const result = await fetchCertificateUrl({ variables: { deviceId: device.id } });
    const url = result.data?.deviceVpnCertificateUrl;
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  const handleCreateVpnCertificate = async () => {
    await createVpnCertificate({ variables: { deviceId: device.id } });
  };
  const electricalPlanUrl = useWatch({ control, name: 'electricalPlanUrl' });
  const diagramUrl = useWatch({ control, name: 'diagramUrl' });

  const handleRelatedDeviceClick = (deviceId: string) => {
    dispatch(
      setSaveHardwareConfigurationModal({
        open: false,
      }),
    );

    setTimeout(() => {
      dispatch(
        setSaveHardwareConfigurationModal({
          open: true,
          deviceId,
        }),
      );
    }, 100);
  };

  const parentDevice = device?.relatedDevices?.find(rel => rel.relation === 'PARENT')?.device;
  const childDevices = device?.relatedDevices
    ?.filter(rel => rel.relation === 'CHILD')
    ?.map(rel => rel.device) || [];

  return (
    <Grid item xs={12} sm={8} p={1} mt={1}>
      <Grid container spacing={1}>
        <Grid item xs={12} mb={1}>
          <Controller
            name="images"
            control={control}
            render={({ field: { onChange, value } }) => (
              <ImagesNews
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file = e.target.files;
                  if (file && Object.keys(file).length > 0) {
                    const url = URL.createObjectURL(file[0]);
                    onChange({ url, file: file[0] });
                  }
                }}
                onDelete={() => {
                  onChange(null);
                }}
                images={value}
                title="Selecciona una imagen o GIF para representar el dispositivo (opcional)"
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} mt={2}>
          <FormInputText
            name="googleDriveUrl"
            control={control}
            label="Google Drive URL"
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} mt={2}>
          <FormInputText
            name="nameProject"
            control={control}
            label="Proyecto *"
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} mt={2}>
          <FormInputText
            name="lockNumber"
            control={control}
            label="Número de chapa / llave"
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} sm={6} mt={2}>
          <FormInputDate
            name="dateAction"
            control={control}
            label="Fecha de activación *"
            disabled={disabled}
          />
        </Grid>
        <Grid item xs={12} mt={2}>
          <FormInputText
            name="observation"
            control={control}
            label="Observaciones generales"
            disabled={disabled}
            multiline
            minRows={4}
          />
        </Grid>
        {device?.hardwareConfiguration?.configurationFile?.url && (
          <Grid item xs={12} mt={2}>
            <LinkMUI
              href={device?.hardwareConfiguration?.configurationFile?.url}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: '#FF9E42' }}
            >
              <Typography>
                DESCARGAR ARCHIVO DE CONFIGURACIÓN ({device?.hardwareConfiguration?.configurationFile?.name})
              </Typography>
              <Typography>
                {device?.hardwareConfiguration?.configurationFile?.lastModified ? `Fecha de última actualización (${dayjs.unix(device?.hardwareConfiguration?.configurationFile?.lastModified).format('DD/MM/YYYY')})` : ''}
              </Typography>
            </LinkMUI>
          </Grid>
        )}

        {/* Plano Eléctrico PDF y Diagrama PDF en línea */}
        <Grid item xs={12} mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <PDFUploadField
                label={_('Plano eléctrico (PDF)')}
                currentUrl={electricalPlanUrl}
                documentType={DeviceDocumentType.ElectricalPlan}
                deviceId={device.id}
                onFileKeyReady={(fileKey, finalUrl) => {
                  setValue('electricalPlanFileKey', fileKey, { shouldDirty: true });
                  setValue('electricalPlanUrl', finalUrl, { shouldDirty: true });
                }}
                onClearFile={() => {
                  setValue('electricalPlanFileKey', null, { shouldDirty: true });
                  setValue('electricalPlanUrl', null, { shouldDirty: true });
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <PDFUploadField
                label={_('Diagrama (PDF)')}
                currentUrl={diagramUrl}
                documentType={DeviceDocumentType.Diagram}
                deviceId={device.id}
                onFileKeyReady={(fileKey, finalUrl) => {
                  setValue('diagramFileKey', fileKey, { shouldDirty: true });
                  setValue('diagramUrl', finalUrl, { shouldDirty: true });
                }}
                onClearFile={() => {
                  setValue('diagramFileKey', null, { shouldDirty: true });
                  setValue('diagramUrl', null, { shouldDirty: true });
                }}
              />
            </Grid>
          </Grid>
        </Grid>


        <Grid item xs={12} mt={2}>
          <Typography variant="h6" mb={1}>
            <Trans>Certificado y VPN</Trans>
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Tooltip title={!certificateExists ? <Trans>Este equipo no tiene certificado VPN disponible</Trans> : ''}>
              <span>
                <Button
                  variant="outlined"
                  color="info"
                  loading={certificateExistsLoading || certificateLoading}
                  disabled={!certificateExistsLoading && !certificateExists}
                  onClick={handleDownloadCertificate}
                >
                  <Trans>Descargar certificado VPN</Trans>
                </Button>
              </span>
            </Tooltip>
            {!certificateExistsLoading && !certificateExists && (
              <Button
                variant="contained"
                color="warning"
                loading={createCertificateLoading}
                disabled={certificateRequested}
                onClick={handleCreateVpnCertificate}
              >
                <Trans>Crear certificado VPN</Trans>
              </Button>
            )}
          </Box>
          {certificateRequested && (
            <Alert severity="info" sx={{ mt: 1.5 }}>
              <Trans>
                La solicitud fue enviada. La creación del certificado puede tardar unos momentos.
                Cierra y vuelve a abrir el modal para verificar si ya fue creado.
              </Trans>
            </Alert>
          )}
        </Grid>

        {(parentDevice || childDevices.length > 0) && (<Grid item xs={12}>
          <Box mb={1} p={2}>
            <Typography variant='h6' color='textSecondary' display='flex' alignItems='center' mb={1}>
              <Link sx={{ mr: 1 }} fontSize='small' />
              Dispositivos relacionados
            </Typography>

            {parentDevice && (
              <Box>
                <Chip
                  label={`ID: ${parentDevice.id}`}
                  onClick={() => handleRelatedDeviceClick(parentDevice.id)}
                  color='primary'
                  clickable
                  sx={{ mr: 1 }}
                />
              </Box>
            )}

            {childDevices.length > 0 && (
              <Box>
                <Box display='flex' flexWrap='wrap' gap={1}>
                  {childDevices.map((child) => (
                    <Chip
                      color='secondary'
                      clickable
                      key={child.id}
                      label={`ID: ${child.id}`}
                      onClick={() => handleRelatedDeviceClick(child.id)}
                    />
                  ))}
                </Box>
              </Box>
            )}
          </Box>
        </Grid>)}
      </Grid>
    </Grid>
  );
};

export default GeneralTabContent;
